import type { TabViewPanelProps } from '@myosh/odin-components';
import clsx from 'clsx';
import { TabPanel, TabView as TabViewPrimeReact } from 'primereact/tabview';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type ReactElement,
} from 'react';

export type TabViewProps = {
  activeIndex?: number;
  onTabChange?: (index: number) => void;
  scrollable?: boolean;
  className?: string;
  children?: ReactElement<TabViewPanelProps> | Array<ReactElement<TabViewPanelProps>>;
  id?: string;
  headerClassName?: string;
  hideBorder?: boolean;
};

export interface TabViewRef {
  setActiveTab: (index: number) => void;
}

const TabView = forwardRef<TabViewRef, TabViewProps>((props, ref) => {
  const {
    activeIndex: controlledActiveIndex = 0,
    scrollable = true,
    className = '',
    children,
    onTabChange,
    id,
    headerClassName,
    hideBorder = false,
  } = props;
  const [activeIndex, setActiveIndex] = useState<number>(controlledActiveIndex);
  const tabViewRef = React.useRef<TabViewPrimeReact>(null);

  useEffect(() => {
    setActiveIndex(controlledActiveIndex);

    requestAnimationFrame(() => {
      const tabViewElement = tabViewRef?.current?.getElement();
      const targetTab = tabViewElement?.querySelectorAll('.p-tabview-nav li')[controlledActiveIndex];

      targetTab?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [controlledActiveIndex]);

  const tabPanels = useMemo(
    () =>
      React.Children.map(children, (tab) => {
        if (!React.isValidElement(tab)) {
          return null;
        }

        return (
          <TabPanel className={id} header={tab.props.header} headerClassName={headerClassName}>
            {tab.props.children}
          </TabPanel>
        );
      }),
    [children, headerClassName, id]
  );

  const updateActiveIndex = (index: number) => {
    setActiveIndex(index);
  };

  useImperativeHandle(ref, () => ({
    setActiveTab: (index: number) => {
      updateActiveIndex(index);
    },
  }));

  const tabViewClassName = clsx('odin tab-view-component', className, {
    'hide-border': hideBorder,
  });

  const onTabChangeHandler = useCallback(
    (event: { index: number }) => {
      const index = event.index;
      if (activeIndex !== index) {
        setActiveIndex(index);
        onTabChange?.(index);
      }
    },
    [activeIndex, onTabChange]
  );

  return (
    <TabViewPrimeReact
      id={id}
      activeIndex={activeIndex}
      scrollable={scrollable}
      pt={{ inkbar: { style: { display: 'none' } } }}
      onTabChange={onTabChangeHandler}
      className={tabViewClassName}
      ref={tabViewRef}
    >
      {tabPanels}
    </TabViewPrimeReact>
  );
});

TabView.displayName = 'TabView';

export default TabView;
